// utils/firestoreUtils.js
import { getAuth } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { getCurrentUserEnv } from '@services/firebase';

const auth = getAuth();

// Serialize Firestore SDK Timestamps to {seconds, nanoseconds} to match Cloud Run output.
const serializeDoc = (data) => {
  if (!data) return data;
  if (typeof data === 'object' && typeof data.toDate === 'function') {
    return { seconds: data.seconds, nanoseconds: data.nanoseconds };
  }
  if (Array.isArray(data)) return data.map(serializeDoc);
  if (typeof data === 'object') {
    return Object.fromEntries(Object.entries(data).map(([k, v]) => [k, serializeDoc(v)]));
  }
  return data;
};

const LIST_COLLECTIONS_URL = 'https://listcollections-o5kcmed2ha-uc.a.run.app';
const READ_FROM_FIRESTORE_URL = 'https://readfromfirestore-o5kcmed2ha-uc.a.run.app';
const WRITE_TO_FIRESTORE_URL = 'https://writetofirestore-o5kcmed2ha-uc.a.run.app';

/*
 * Verifies connectivity to Firestore by reading the cUsers collection.
 * @returns {Promise<boolean>} - True if connection is successful.
 * @throws {Error} - If connection fails or user is not authenticated.
 */
const establishDbConnection = async () => {
  // In Test env, verify connectivity via the Test Cloud Function.
  if (getCurrentUserEnv() === 'Test') {
    const response = await fetch(`${TEST_FUNCTIONS_BASE}/readFromFirestoreTest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetCollection: 'cUsers', fetchDocIds: true }),
    });
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.error?.message || 'Failed to connect to Test database.');
    return true;
  }

  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated.');
    }
    const idToken = await user.getIdToken();

    const response = await fetch(READ_FROM_FIRESTORE_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        targetCollection: 'cUsers',
        fetchDocIds: true,
      }),
    });

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to connect to database using cUsers collection.');
    }

    return true;
  } catch (err) {
    console.error('Error establishing database connection:', err.message);
    throw err;
  }
};

/*
 * Fetches the list of Firestore collections accessible to the user.
 * @returns {Promise<Array<string>>} - List of collection names.
 * @throws {Error} - If fetch fails or user is not authenticated.
 */
const TEST_FUNCTIONS_BASE = 'https://us-central1-pcakedatastoretest.cloudfunctions.net';

const fetchFirestoreCollections = async () => {
  if (getCurrentUserEnv() === 'Test') {
    const response = await fetch(`${TEST_FUNCTIONS_BASE}/listCollectionsTest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.error?.message || 'Failed to list Test collections.');
    return result.collections || [];
  }

  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated.');
    }
    const idToken = await user.getIdToken();

    const response = await fetch(LIST_COLLECTIONS_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to fetch collections.');
    }

    return result.collections || [];
  } catch (err) {
    console.error('Error fetching Firestore collections:', err.message);
    throw err;
  }
};

/*
 * Reads data from a Firestore collection or a specific document.
 * @param {string} targetCollection - The Firestore collection to read from.
 * @param {Object} options - Options for the read operation.
 * @param {string} [options.docId] - The ID of a specific document to read (optional).
 * @param {boolean} [options.fetchDocIds=false] - Whether to fetch only document IDs (optional).
 * @returns {Promise<Array|Object>} - The fetched data or document IDs.
 * @throws {Error} - If read fails or user is not authenticated.
 */
const readFirestoreCollection = async (targetCollection, { docId = null, fetchDocIds = false } = {}) => {
  if (getCurrentUserEnv() === 'Test') {
    const response = await fetch(`${TEST_FUNCTIONS_BASE}/readFromFirestoreTest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetCollection, docId, fetchDocIds }),
    });
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.error?.message || `Failed to read ${targetCollection}.`);
    return result.data;
  }

  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated.');
    }
    const idToken = await user.getIdToken();

    const requestBody = {
      targetCollection,
      fetchDocIds,
    };
    if (docId) {
      requestBody.docId = docId;
    }

    const response = await fetch(READ_FROM_FIRESTORE_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || `Failed to read from ${targetCollection}${docId ? ` (docId: ${docId})` : ''}.`);
    }

    return result.data;
  } catch (err) {
    console.error(`Error reading Firestore collection ${targetCollection}${docId ? ` (docId: ${docId})` : ''}:`, err.message);
    throw err;
  }
};

/*
 * Fetches document IDs from a specified Firestore collection.
 * @param {string} targetCollection - The Firestore collection to fetch document IDs from.
 * @returns {Promise<Array<string>>} - List of document IDs.
 * @throws {Error} - If fetch fails or user is not authenticated.
 */
const fetchCollectionDocIds = async (targetCollection) => {
  const docIds = await readFirestoreCollection(targetCollection, { fetchDocIds: true });
  return docIds || [];
};

/*
 * Fetches a specific document from a Firestore collection by its ID.
 * @param {string} targetCollection - The Firestore collection containing the document.
 * @param {string} docId - The ID of the document to fetch.
 * @returns {Promise<Object|null>} - The document data, or null if not found.
 * @throws {Error} - If fetch fails or user is not authenticated.
 */
const fetchDocumentById = async (targetCollection, docId) => {
  const docData = await readFirestoreCollection(targetCollection, { docId });
  return docData || null;
};

/*
 * Writes data to a Firestore collection.
 * @param {string} targetCollection - The Firestore collection to write to.
 * @param {Array} data - The data to write.
 * @param {boolean} isOtemplate - Whether the collection is an otemplate collection.
 * @returns {Promise<void>}
 * @throws {Error} - If write fails or user is not authenticated.
 */
const writeToFirestore = async (targetCollection, data, isOtemplate) => {
  if (getCurrentUserEnv() === 'Test') {
    const response = await fetch(`${TEST_FUNCTIONS_BASE}/writeToFirestoreTest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetCollection, data, isOtemplate }),
    });
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.error?.message || 'Failed to write to Test Firestore.');
    return;
  }

  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated.');
    }
    const idToken = await user.getIdToken();

    const response = await fetch(WRITE_TO_FIRESTORE_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        targetCollection,
        data,
        isOtemplate,
      }),
    });

    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error?.message || 'Failed to save to Firestore.');
    }
  } catch (err) {
    console.error('Error saving to Firestore:', err.message);
    throw err;
  }
};

export {
  establishDbConnection,
  fetchFirestoreCollections,
  readFirestoreCollection,
  fetchCollectionDocIds,
  fetchDocumentById,
  writeToFirestore,
  writeToFirestoreSdk,
};

/**
 * Write records directly to Firestore via the SDK (no Cloud Run backend).
 * Intended for writes to the Test project where no Cloud Run service exists.
 * Each record in `data` must have an `id` field used as the document ID.
 *
 * @param {string} targetCollection - Firestore collection name.
 * @param {Array<{id: string, ...}>} data - Records to write.
 * @param {import('firebase/firestore').Firestore} db - Firestore instance to target.
 */
async function writeToFirestoreSdk(targetCollection, data, db) {
  if (!db) throw new Error('No Firestore db instance provided.');
  if (!Array.isArray(data) || data.length === 0) throw new Error('No data to write.');
  for (const record of data) {
    const { id, ...fields } = record;
    if (id === undefined || id === null) throw new Error('Each record must have an id field.');
    const docRef = doc(db, targetCollection, String(id));
    await setDoc(docRef, { ...fields, _savedAt: new Date().toISOString() }, { merge: true });
  }
}